import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {
  listTaskImports,
  fetchTaskImport,
  remapTaskImportRow,
  setTaskImportFlight,
  commitTaskImport,
  discardTaskImport,
  listAvatars,
  listPainPoints,
  listAngleChoices,
  listHookTypeChoices,
  listAdTypeChoices,
  listStyleChoices,
  listDesignerProfiles,
  listDesignerGroups,
  listFlights,
} from '@src/api/creativeStudio';
import { fetchVerticals } from '@src/api/actions/verticals';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';

const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'All Ages' },
  { value: '55+', label: '55+' },
  { value: 'men', label: 'Men' },
  { value: 'women', label: 'Women' },
];

// Creative-dimension fields shown in the expanded detail row.
const DIM_FIELDS = [
  { key: 'avatar', label: 'Avatar', scoped: true },
  { key: 'pain_point', label: 'Pain Point', scoped: true },
  { key: 'angle', label: 'Angle' },
  { key: 'hook_type', label: 'Hook Type' },
  { key: 'ad_type', label: 'Ad Type' },
  { key: 'style', label: 'Style' },
];

const errMsg = (err, fallback) =>
  err?.response?.data?.detail || err?.response?.data?.error || fallback;

const arr = (res) => (Array.isArray(res?.data) ? res.data : []);

// A small status glyph for a field given its match_status entry.
function MatchGlyph({ status }) {
  if (status === 'matched') {
    return <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />;
  }
  if (status === 'unmatched' || status === 'ambiguous') {
    return (
      <Tooltip title={status === 'ambiguous' ? 'Multiple matches — pick one' : "Couldn't match — pick one"}>
        <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main' }} />
      </Tooltip>
    );
  }
  return null;
}

export default function TaskImportReviewModal({ open, onClose, onCommitted }) {
  const [batches, setBatches] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingRowId, setSavingRowId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [remember, setRemember] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [opts, setOpts] = useState({
    verticals: [], avatars: [], painPoints: [], angles: [],
    hookTypes: [], adTypes: [], styles: [], designers: [], designerGroups: [], flights: [],
  });

  const {
    options: clientOptions,
    loading: clientOptionsLoading,
    handleInputChange: handleClientSearch,
  } = useClientAutocomplete();

  const loadBatches = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listTaskImports({ status: 'pending' });
      const list = arr(res);
      setBatches(list);
      setSelectedId((prev) => (list.some((b) => b.id === prev) ? prev : list[0]?.id || null));
    } catch (err) {
      setError(errMsg(err, 'Failed to load pending imports'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (id) => {
    if (!id) {
      setBatch(null);
      return;
    }
    try {
      const res = await fetchTaskImport(id);
      setBatch(res.data);
    } catch (err) {
      setError(errMsg(err, 'Failed to load import'));
    }
  }, []);

  // Load option lists + pending batches when the modal opens.
  useEffect(() => {
    if (!open) return;
    loadBatches();
    (async () => {
      try {
        const [v, av, pp, an, ht, at, st, de, dg, fl] = await Promise.all([
          fetchVerticals(), listAvatars(), listPainPoints(), listAngleChoices(),
          listHookTypeChoices(), listAdTypeChoices(), listStyleChoices(),
          listDesignerProfiles(), listDesignerGroups(), listFlights(),
        ]);
        setOpts({
          verticals: arr(v), avatars: arr(av), painPoints: arr(pp), angles: arr(an),
          hookTypes: arr(ht), adTypes: arr(at), styles: arr(st),
          designers: arr(de), designerGroups: arr(dg), flights: arr(fl),
        });
      } catch (_e) { /* keep usable */ }
    })();
  }, [open, loadBatches]);

  useEffect(() => {
    if (open) loadDetail(selectedId);
  }, [open, selectedId, loadDetail]);

  const remap = useCallback(async (row, payload) => {
    setSavingRowId(row.id);
    setError('');
    try {
      await remapTaskImportRow(batch.id, row.id, { ...payload, save_aliases: remember });
      await loadDetail(batch.id);
    } catch (err) {
      setError(errMsg(err, 'Remap failed'));
    } finally {
      setSavingRowId(null);
    }
  }, [batch, remember, loadDetail]);

  const handleSetFlight = useCallback(async (flightId) => {
    setBusy(true);
    setError('');
    try {
      await setTaskImportFlight(batch.id, flightId || '');
      await loadDetail(batch.id);
    } catch (err) {
      setError(errMsg(err, 'Could not set flight'));
    } finally {
      setBusy(false);
    }
  }, [batch, loadDetail]);

  const handleCommit = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const res = await commitTaskImport(batch.id);
      onCommitted?.(res.data);
      const remaining = batches.filter((b) => b.id !== batch.id);
      setBatches(remaining);
      if (remaining.length) {
        setSelectedId(remaining[0].id);
      } else {
        onClose?.();
      }
    } catch (err) {
      setError(errMsg(err, 'Commit failed — some rows still need mapping'));
    } finally {
      setBusy(false);
    }
  }, [batch, batches, onCommitted, onClose]);

  const handleDiscard = useCallback(async () => {
    if (!window.confirm('Discard this import? The staged tasks will not be created.')) return;
    setBusy(true);
    try {
      await discardTaskImport(batch.id);
      const remaining = batches.filter((b) => b.id !== batch.id);
      setBatches(remaining);
      if (remaining.length) setSelectedId(remaining[0].id);
      else onClose?.();
    } catch (err) {
      setError(errMsg(err, 'Discard failed'));
    } finally {
      setBusy(false);
    }
  }, [batch, batches, onClose]);

  const dimOptions = (key, row) => {
    const vId = row.resolved.vertical?.id;
    switch (key) {
      case 'angle': return opts.angles.map((o) => ({ id: o.id, label: o.label }));
      case 'hook_type': return opts.hookTypes.map((o) => ({ id: o.id, label: o.label }));
      case 'ad_type': return opts.adTypes.map((o) => ({ id: o.id, label: o.label }));
      case 'style': return opts.styles.map((o) => ({ id: o.id, label: o.label }));
      case 'avatar':
        return opts.avatars.filter((o) => o.vertical === vId).map((o) => ({ id: o.id, label: o.name }));
      case 'pain_point':
        return opts.painPoints.filter((o) => o.vertical === vId).map((o) => ({ id: o.id, label: o.label }));
      default: return [];
    }
  };

  const rows = batch?.rows || [];
  const pendingCount = batches.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1}>
          <span>Review imported tasks</span>
          {pendingCount > 1 ? (
            <TextField
              size="small" select label="Import batch" sx={{ minWidth: 240 }}
              value={selectedId || ''} onChange={(e) => setSelectedId(Number(e.target.value))}
            >
              {batches.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  #{b.id} · {b.source || 'api'} · {b.row_count} rows
                  {b.needs_attention_count ? ` · ${b.needs_attention_count} to fix` : ' · ready'}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert> : null}

        {loading ? (
          <Stack alignItems="center" py={6}><CircularProgress /></Stack>
        ) : !batch ? (
          <Typography color="text.secondary" align="center" py={6}>No imports waiting for review.</Typography>
        ) : (
          <>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Source: <strong>{batch.source || 'api'}</strong> · {batch.row_count} task(s)
              </Typography>
              {batch.needs_attention_count ? (
                <Chip size="small" color="warning" icon={<WarningAmberIcon />} label={`${batch.needs_attention_count} need mapping`} />
              ) : (
                <Chip size="small" color="success" icon={<CheckCircleIcon />} label="All mapped — ready to commit" />
              )}
              <Box flexGrow={1} />
              <FormControlLabel
                control={<Switch size="small" checked={remember} onChange={(e) => setRemember(e.target.checked)} />}
                label="Remember my mappings for next time"
              />
            </Stack>

            {/* Batch-level flight mapping (only if a flight name was sent but not resolved). */}
            {batch.flight_raw && !batch.flight ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  <span>Flight “{batch.flight_raw}” didn’t match. Pick one:</span>
                  <TextField
                    size="small" select sx={{ minWidth: 220 }} value="" disabled={busy}
                    onChange={(e) => handleSetFlight(e.target.value)}
                  >
                    {opts.flights.map((f) => (
                      <MenuItem key={f.id} value={f.id}>{f.flight_code} · {f.name}</MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </Alert>
            ) : null}

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={36} />
                  <TableCell>#</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Vertical</TableCell>
                  <TableCell>Designer</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Skip</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const saving = savingRowId === row.id;
                  const open_ = !!expanded[row.id];
                  const ms = row.match_status || {};
                  const clientValue = row.resolved.client
                    ? { value: row.resolved.client.id, label: row.resolved.client.label }
                    : null;
                  return (
                    <React.Fragment key={row.id}>
                      <TableRow sx={{ opacity: row.excluded ? 0.4 : 1, '& td': { borderBottom: open_ ? 'none' : undefined } }}>
                        <TableCell>
                          <IconButton size="small" onClick={() => setExpanded((p) => ({ ...p, [row.id]: !open_ }))}>
                            {open_ ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{row.row_index + 1}</TableCell>

                        {/* Client (searchable) */}
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <MatchGlyph status={ms.client} />
                            <Autocomplete
                              size="small" sx={{ minWidth: 200 }} disabled={saving || row.excluded}
                              options={clientOptions} loading={clientOptionsLoading} value={clientValue}
                              getOptionLabel={(o) => o.label || ''}
                              isOptionEqualToValue={(o, v) => o.value === v.value}
                              onInputChange={(_e, v) => handleClientSearch(v)}
                              onChange={(_e, v) => v && remap(row, { client: v.value })}
                              renderInput={(params) => (
                                <TextField {...params} placeholder={row.raw?.client || 'Pick client'} variant="standard" />
                              )}
                            />
                          </Stack>
                        </TableCell>

                        {/* Vertical */}
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <MatchGlyph status={ms.vertical} />
                            <TextField
                              size="small" select variant="standard" sx={{ minWidth: 120 }}
                              disabled={saving || row.excluded}
                              value={row.resolved.vertical?.id || ''}
                              onChange={(e) => remap(row, { vertical: e.target.value })}
                            >
                              <MenuItem value=""><em>{row.raw?.vertical || '—'}</em></MenuItem>
                              {opts.verticals.map((v) => <MenuItem key={v.id} value={v.id}>{v.vertical}</MenuItem>)}
                            </TextField>
                          </Stack>
                        </TableCell>

                        {/* Designer */}
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <MatchGlyph status={ms.designer} />
                            <TextField
                              size="small" select variant="standard" sx={{ minWidth: 130 }}
                              disabled={saving || row.excluded}
                              value={row.resolved.designer?.id || ''}
                              onChange={(e) => remap(row, { designer: e.target.value })}
                            >
                              <MenuItem value=""><em>{row.raw?.designer || 'Unassigned'}</em></MenuItem>
                              {opts.designers.map((d) => (
                                <MenuItem key={d.id} value={d.id}>{d.display_name || d.slug}</MenuItem>
                              ))}
                            </TextField>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          {saving ? <CircularProgress size={16} /> : row.is_clean
                            ? <Chip size="small" color="success" variant="outlined" label="ready" />
                            : <Chip size="small" color="warning" variant="outlined" label="needs mapping" />}
                        </TableCell>

                        <TableCell align="right">
                          <Tooltip title={row.excluded ? 'Include this row' : 'Skip this row on commit'}>
                            <IconButton
                              size="small" color={row.excluded ? 'default' : 'error'} disabled={saving}
                              onClick={() => remap(row, { excluded: !row.excluded })}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>

                      {/* Expanded detail: creative dimensions + audience + script preview */}
                      <TableRow>
                        <TableCell colSpan={7} sx={{ py: 0, borderBottom: open_ ? undefined : 'none' }}>
                          <Collapse in={open_} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 1.5, pl: 5 }}>
                              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                                {DIM_FIELDS.map((f) => {
                                  const options = dimOptions(f.key, row);
                                  const scopedDisabled = f.scoped && !row.resolved.vertical;
                                  return (
                                    <Stack key={f.key} direction="row" spacing={0.5} alignItems="center">
                                      <MatchGlyph status={ms[f.key]} />
                                      <TextField
                                        size="small" select label={f.label} sx={{ minWidth: 150 }}
                                        disabled={saving || row.excluded || scopedDisabled}
                                        helperText={scopedDisabled ? 'set vertical first' : (row.raw?.[f.key] || '')}
                                        value={row.resolved[f.key]?.id || ''}
                                        onChange={(e) => remap(row, { [f.key]: e.target.value })}
                                      >
                                        <MenuItem value=""><em>—</em></MenuItem>
                                        {options.map((o) => <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>)}
                                      </TextField>
                                    </Stack>
                                  );
                                })}
                                <TextField
                                  size="small" select label="Audience" sx={{ minWidth: 130 }}
                                  disabled={saving || row.excluded}
                                  value={row.audience || ''}
                                  onChange={(e) => remap(row, { audience: e.target.value })}
                                >
                                  <MenuItem value=""><em>—</em></MenuItem>
                                  {AUDIENCE_OPTIONS.map((a) => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
                                </TextField>
                              </Stack>

                              {row.hook_text ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                  <strong>Hook:</strong> {row.hook_text}
                                </Typography>
                              ) : null}
                              {row.script_text ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                  <strong>Script{row.is_custom_script ? ' (custom)' : ''}:</strong>{' '}
                                  {row.script_text.length > 280 ? `${row.script_text.slice(0, 280)}…` : row.script_text}
                                </Typography>
                              ) : null}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button color="error" onClick={handleDiscard} disabled={!batch || busy}>Discard</Button>
        <Box flexGrow={1} />
        <Button onClick={onClose} disabled={busy}>Close</Button>
        <Button
          variant="contained" onClick={handleCommit}
          disabled={!batch || busy || !batch.is_clean}
        >
          {busy ? <CircularProgress size={18} /> : `Commit ${rows.filter((r) => !r.excluded).length} task(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
